import React, { useState, useEffect } from 'react';
import collegeConfig from '../../config/collegeConfig';
import {
  FaUserMd, FaPlus, FaEdit, FaTrash, FaSearch, FaHospital,
  FaEnvelope, FaTimes, FaCheckCircle, FaTimesCircle, FaSave,
  FaLock, FaSpinner, FaEye, FaEyeSlash, FaToggleOn, FaToggleOff
} from 'react-icons/fa';

const API = collegeConfig.api.baseURL;
const token = () => localStorage.getItem('token');
const headers = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` });

const ManageDRPIncharge = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null); // null | 'edit' | 'create' | 'password'
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [showPw, setShowPw] = useState(false);

  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', hospitalName: '', isActive: true });
  const [pwForm, setPwForm] = useState({ newPassword: '', confirmPassword: '' });

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/students/all-users?role=drp_incharge`, { headers: { Authorization: `Bearer ${token()}` } });
      const d = await r.json();
      if (d.success) setUsers(d.users || []);
      else setError(d.message || 'Failed to load');
    } catch { setError('Failed to load DRP Incharge users'); }
    setLoading(false);
  };

  useEffect(() => { fetchUsers(); }, []);

  const openCreate = () => {
    setForm({ firstName: '', lastName: '', email: '', hospitalName: '', isActive: true, password: '' });
    setModal('create');
  };

  const openEdit = (u) => {
    setSelected(u);
    setForm({
      firstName: u.profile?.firstName || '',
      lastName: u.profile?.lastName || '',
      email: u.email || '',
      hospitalName: u.drpInchargeDetails?.hospitalName || '',
      isActive: u.isActive !== false
    });
    setModal('edit');
  };

  const openPassword = (u) => {
    setSelected(u);
    setPwForm({ newPassword: '', confirmPassword: '' });
    setModal('password');
  };

  const handleCreate = async () => {
    const { firstName, lastName, email, hospitalName, password } = form;
    if (!firstName || !lastName || !email || !password || !hospitalName) {
      showToast('All fields are required'); return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API}/students/create-user`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          email,
          password,
          role: 'drp_incharge',
          profile: { firstName, lastName },
          drpInchargeDetails: { hospitalName }
        })
      });
      const d = await r.json();
      if (d.success) { showToast('Incharge account created'); setModal(null); fetchUsers(); }
      else showToast(d.message || 'Failed to create');
    } catch { showToast('Failed to create'); }
    setSaving(false);
  };

  const handleEdit = async () => {
    const { firstName, lastName, email, hospitalName, isActive } = form;
    if (!firstName || !lastName || !email) { showToast('Name and email are required'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/students/update-user/${selected._id}`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({
          email,
          profile: { ...selected.profile, firstName, lastName },
          drpInchargeDetails: { hospitalName },
          isActive
        })
      });
      const d = await r.json();
      if (d.success) { showToast('Updated successfully'); setModal(null); fetchUsers(); }
      else showToast(d.message || 'Failed to update');
    } catch { showToast('Failed to update'); }
    setSaving(false);
  };

  const handleChangePassword = async () => {
    if (!pwForm.newPassword || pwForm.newPassword.length < 6) { showToast('Password must be at least 6 characters'); return; }
    if (pwForm.newPassword !== pwForm.confirmPassword) { showToast('Passwords do not match'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/students/update-user/${selected._id}`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ password: pwForm.newPassword })
      });
      const d = await r.json();
      if (d.success) { showToast('Password changed successfully'); setModal(null); }
      else showToast(d.message || 'Failed to change password');
    } catch { showToast('Failed to change password'); }
    setSaving(false);
  };

  const handleToggleActive = async (u) => {
    try {
      const r = await fetch(`${API}/students/update-user/${u._id}`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ isActive: !u.isActive })
      });
      const d = await r.json();
      if (d.success) { showToast(`Account ${!u.isActive ? 'activated' : 'deactivated'}`); fetchUsers(); }
      else showToast(d.message || 'Failed');
    } catch { showToast('Failed to toggle status'); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete DRP Incharge account for "${u.profile?.firstName} ${u.profile?.lastName}"? This cannot be undone.`)) return;
    try {
      const r = await fetch(`${API}/students/delete-user/${u._id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` }
      });
      const d = await r.json();
      if (d.success) { showToast('Deleted successfully'); fetchUsers(); }
      else showToast(d.message || 'Failed to delete');
    } catch { showToast('Failed to delete'); }
  };

  const filtered = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    const name = `${u.profile?.firstName || ''} ${u.profile?.lastName || ''}`.toLowerCase();
    return name.includes(q) || (u.email || '').toLowerCase().includes(q) || (u.drpInchargeDetails?.hospitalName || '').toLowerCase().includes(q);
  });

  const active = users.filter(u => u.isActive !== false).length;

  return (
    <div style={s.container}>
      {toast && <div style={s.toast}>{toast}</div>}

      {/* Header */}
      <div style={s.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <FaUserMd style={{ fontSize: 24, color: '#E67E22' }} />
          <div>
            <h1 style={s.title}>Manage DRP Incharge</h1>
            <p style={s.subtitle}>Incharge accounts created by students for Classic DRP</p>
          </div>
        </div>
        <button onClick={openCreate} style={s.createBtn}><FaPlus style={{ marginRight: 6 }} />Create Incharge</button>
      </div>

      {/* Stats */}
      <div style={s.statsRow}>
        <div style={s.statCard}><span style={{ ...s.statNum, color: '#E67E22' }}>{users.length}</span><span style={s.statLabel}>Total Incharges</span></div>
        <div style={s.statCard}><span style={{ ...s.statNum, color: '#059669' }}>{active}</span><span style={s.statLabel}>Active</span></div>
        <div style={s.statCard}><span style={{ ...s.statNum, color: '#dc2626' }}>{users.length - active}</span><span style={s.statLabel}>Inactive</span></div>
        <div style={s.statCard}><span style={{ ...s.statNum, color: '#2563eb' }}>{[...new Set(users.map(u => u.drpInchargeDetails?.hospitalName).filter(Boolean))].length}</span><span style={s.statLabel}>Hospitals</span></div>
      </div>

      {/* Search */}
      <div style={s.searchWrap}>
        <FaSearch style={{ position: 'absolute', left: 12, top: 11, color: '#999' }} />
        <input
          type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, email or hospital..."
          style={s.searchInput}
        />
      </div>

      {error && <div style={s.errorBanner}>{error}</div>}

      {loading ? (
        <div style={s.centered}><FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: 28, color: '#E67E22' }} /></div>
      ) : filtered.length === 0 ? (
        <div style={s.emptyState}>
          <FaUserMd style={{ fontSize: 48, color: '#ddd', marginBottom: 12 }} />
          <p style={{ color: '#999' }}>No DRP Incharge accounts found.</p>
          <p style={{ color: '#bbb', fontSize: 13 }}>They are created by students when setting up their Classic DRP profile.</p>
        </div>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                {['Incharge', 'Email', 'Hospital', 'Status', 'Created', 'Actions'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => {
                const name = `${u.profile?.firstName || ''} ${u.profile?.lastName || ''}`.trim();
                return (
                  <tr key={u._id} style={s.tr}>
                    <td style={s.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={s.avatar}>{name.charAt(0).toUpperCase() || 'I'}</div>
                        <span style={{ fontWeight: 600 }}>{name || '—'}</span>
                      </div>
                    </td>
                    <td style={s.td}><FaEnvelope size={11} style={{ marginRight: 6, color: '#888' }} />{u.email}</td>
                    <td style={s.td}><FaHospital size={11} style={{ marginRight: 6, color: '#888' }} />{u.drpInchargeDetails?.hospitalName || '—'}</td>
                    <td style={s.td}>
                      <span style={{ ...s.badge, background: u.isActive !== false ? '#d1fae5' : '#fee2e2', color: u.isActive !== false ? '#059669' : '#dc2626' }}>
                        {u.isActive !== false ? <FaCheckCircle style={{ marginRight: 4 }} /> : <FaTimesCircle style={{ marginRight: 4 }} />}
                        {u.isActive !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={s.td}>{u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-IN') : '—'}</td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => openEdit(u)} style={s.iconBtn} title="Edit"><FaEdit /></button>
                        <button onClick={() => openPassword(u)} style={{ ...s.iconBtn, color: '#7c3aed' }} title="Change Password"><FaLock /></button>
                        <button onClick={() => handleToggleActive(u)} style={{ ...s.iconBtn, color: u.isActive !== false ? '#dc2626' : '#059669' }} title={u.isActive !== false ? 'Deactivate' : 'Activate'}>
                          {u.isActive !== false ? <FaToggleOff /> : <FaToggleOn />}
                        </button>
                        <button onClick={() => handleDelete(u)} style={{ ...s.iconBtn, color: '#dc2626' }} title="Delete"><FaTrash /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {modal === 'create' && (
        <div style={s.overlay} onClick={() => setModal(null)}>
          <div style={s.modalBox} onClick={e => e.stopPropagation()}>
            <ModalHeader title="Create DRP Incharge Account" onClose={() => setModal(null)} />
            <div style={s.modalBody}>
              <div style={s.formGrid}>
                {[
                  { key: 'firstName', label: 'First Name', required: true },
                  { key: 'lastName', label: 'Last Name', required: true },
                  { key: 'email', label: 'Email', type: 'email', required: true },
                  { key: 'hospitalName', label: 'Hospital Name', required: true },
                  { key: 'password', label: 'Password', type: 'password', required: true }
                ].map(f => (
                  <div key={f.key}>
                    <label style={s.label}>{f.label}{f.required && ' *'}</label>
                    <input type={f.type || 'text'} value={form[f.key] || ''} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} style={s.input} />
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button onClick={handleCreate} disabled={saving} style={s.primaryBtn}>
                  {saving ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <><FaSave style={{ marginRight: 6 }} />Create</>}
                </button>
                <button onClick={() => setModal(null)} style={s.secondaryBtn}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {modal === 'edit' && (
        <div style={s.overlay} onClick={() => setModal(null)}>
          <div style={s.modalBox} onClick={e => e.stopPropagation()}>
            <ModalHeader title="Edit DRP Incharge" onClose={() => setModal(null)} />
            <div style={s.modalBody}>
              <div style={s.formGrid}>
                {[
                  { key: 'firstName', label: 'First Name', required: true },
                  { key: 'lastName', label: 'Last Name', required: true },
                  { key: 'email', label: 'Email', type: 'email', required: true },
                  { key: 'hospitalName', label: 'Hospital Name' }
                ].map(f => (
                  <div key={f.key}>
                    <label style={s.label}>{f.label}{f.required && ' *'}</label>
                    <input type={f.type || 'text'} value={form[f.key] || ''} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} style={s.input} />
                  </div>
                ))}
                <div>
                  <label style={s.label}>Status</label>
                  <select value={form.isActive ? 'active' : 'inactive'} onChange={e => setForm(p => ({ ...p, isActive: e.target.value === 'active' }))} style={s.input}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button onClick={handleEdit} disabled={saving} style={s.primaryBtn}>
                  {saving ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <><FaSave style={{ marginRight: 6 }} />Save Changes</>}
                </button>
                <button onClick={() => setModal(null)} style={s.secondaryBtn}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {modal === 'password' && (
        <div style={s.overlay} onClick={() => setModal(null)}>
          <div style={{ ...s.modalBox, maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <ModalHeader title={`Change Password — ${selected?.profile?.firstName || ''}`} onClose={() => setModal(null)} />
            <div style={s.modalBody}>
              <div style={{ marginBottom: 14 }}>
                <label style={s.label}>New Password *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={pwForm.newPassword}
                    onChange={e => setPwForm(p => ({ ...p, newPassword: e.target.value }))}
                    placeholder="Minimum 6 characters"
                    style={{ ...s.input, paddingRight: 40 }}
                  />
                  <button onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: 10, top: 10, background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}>
                    {showPw ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={s.label}>Confirm New Password *</label>
                <input type="password" value={pwForm.confirmPassword} onChange={e => setPwForm(p => ({ ...p, confirmPassword: e.target.value }))} style={s.input} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={handleChangePassword} disabled={saving} style={s.primaryBtn}>
                  {saving ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <><FaLock style={{ marginRight: 6 }} />Change Password</>}
                </button>
                <button onClick={() => setModal(null)} style={s.secondaryBtn}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from{transform:rotate(0deg);}to{transform:rotate(360deg);}}`}</style>
    </div>
  );
};

const ModalHeader = ({ title, onClose }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
    <span style={{ fontWeight: 700, fontSize: 16 }}>{title}</span>
    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#666' }}><FaTimes /></button>
  </div>
);

const s = {
  container: { padding: '0 0 32px', fontFamily: 'sans-serif' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  title: { margin: 0, fontSize: 22, fontWeight: 800, color: '#1a1a2e' },
  subtitle: { margin: '4px 0 0', fontSize: 13, color: '#666' },
  createBtn: { display: 'flex', alignItems: 'center', background: '#E67E22', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontWeight: 700, fontSize: 14 },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 },
  statCard: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 18px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  statNum: { display: 'block', fontSize: 26, fontWeight: 800, color: '#1a1a2e' },
  statLabel: { fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' },
  searchWrap: { position: 'relative', marginBottom: 16 },
  searchInput: { width: '100%', padding: '9px 12px 9px 36px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' },
  errorBanner: { padding: 12, background: '#fee2e2', color: '#dc2626', borderRadius: 8, marginBottom: 16, fontSize: 14 },
  centered: { display: 'flex', justifyContent: 'center', padding: 40 },
  emptyState: { textAlign: 'center', padding: '60px 20px' },
  tableWrap: { background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '12px 14px', fontSize: 14, color: '#374151', verticalAlign: 'middle' },
  avatar: { width: 34, height: 34, borderRadius: '50%', background: '#E67E22', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 },
  badge: { display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600 },
  iconBtn: { background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', cursor: 'pointer', color: '#666', display: 'flex', alignItems: 'center' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
  modalBox: { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  modalBody: { padding: '20px' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 },
  input: { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' },
  primaryBtn: { background: '#E67E22', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontWeight: 700, display: 'flex', alignItems: 'center', fontSize: 14 },
  secondaryBtn: { background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14 },
  toast: { position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#1a1a2e', color: '#fff', padding: '10px 20px', borderRadius: 20, fontSize: 14, zIndex: 2000, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }
};

export default ManageDRPIncharge;
